import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, CanDeactivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Selectors as ProfileSelectors } from 'features/profiles-state/+state/profiles-state.state';
import { UserProfile } from 'services/user/user.interface';
import {
  IPermissions,
  IPolicies,
  PolicyPermissionsService,
} from 'services/policy-permission/policy-permissions.service';

@Injectable()
export class TestingWorkflowGuardService  {
  constructor(private store: Store<any>) {}

  canActivate(): Observable<boolean> {
    return this.store.select(ProfileSelectors.currentProfile).pipe(
      map((currentProfile: UserProfile) => {
        const { modules } = currentProfile;
        if (modules) {
          const { testingWorkflow, sharedTestingWorkflow } = modules;
          return testingWorkflow || sharedTestingWorkflow;
        }
        return false;
      }),
    );
  }
}

export const testingWorkflowGuard: CanActivateFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
    return inject(TestingWorkflowGuardService).canActivate();
};

@Injectable()
export class WorkflowModuleCanDeactivateGuardService  {
  canDeactivate(component) {
    return component && component.canDeactivate ? component.canDeactivate() : true;
  }
}

export const workflowModuleCanDeactivateGuard: CanDeactivateFn<any> = (
    component: any,
    currentRoute: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState: RouterStateSnapshot
) => {
    return inject(WorkflowModuleCanDeactivateGuardService).canDeactivate(component);
};

export type COMPLIANCE_VERIFICATION_LIST_POLICIES_KEYS =
  | 'complianceWorkflowVerificationList'
  | 'complianceWorkflowPrimaryVerification'
  | 'complianceWorkflowSecondaryVerification';

export const COMPLIANCE_VERIFICATION_LIST_POLICIES: IPolicies<COMPLIANCE_VERIFICATION_LIST_POLICIES_KEYS> = {
  complianceWorkflowVerificationList: {
    name: 'complianceWorkflowVerificationList',
    url: 'compliance/verification_list/all',
    action: 'access',
  },
  complianceWorkflowPrimaryVerification: {
    name: 'complianceWorkflowPrimaryVerification',
    url: 'compliance/primary_verification/all',
    action: 'access',
  },
  complianceWorkflowSecondaryVerification: {
    name: 'complianceWorkflowSecondaryVerification',
    url: 'compliance/secondary_verification/all',
    action: 'access',
  },
};

@Injectable()
export class WorkflowComplianceVerificationListPolicyGuardService  {
  constructor(private router: Router, private policyPermissionsService: PolicyPermissionsService) {}

  canActivate() {
    return this.policyPermissionsService
      .hasMultiplePermissions(Object.values(COMPLIANCE_VERIFICATION_LIST_POLICIES))
      .pipe(
        take(1),
        map((permissions: IPermissions<COMPLIANCE_VERIFICATION_LIST_POLICIES_KEYS>) => {
          if (
            permissions.complianceWorkflowVerificationList &&
            (permissions.complianceWorkflowPrimaryVerification || permissions.complianceWorkflowSecondaryVerification)
          ) {
            return true;
          }

          this.router.navigate(['/access-restricted']);
          return false;
        }),
      );
  }
}

export const workflowComplianceVerificationListPolicyGuard: CanActivateFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
    return inject(WorkflowComplianceVerificationListPolicyGuardService).canActivate();
};

export type CERTEMY_TASKS_VERIFICATION_LIST_POLICIES_KEYS =
  | 'certemyToDoWorkflowVerificationList'
  | 'certemyToDoWorkflowPrimaryVerification'
  | 'certemyToDoWorkflowSecondaryVerification';

export const CERTEMY_TASKS_VERIFICATION_LIST_POLICIES: IPolicies<CERTEMY_TASKS_VERIFICATION_LIST_POLICIES_KEYS> = {
  certemyToDoWorkflowVerificationList: {
    name: 'certemyToDoWorkflowVerificationList',
    url: 'management/certemy_tasks/todo',
    action: 'access',
  },
  certemyToDoWorkflowPrimaryVerification: {
    name: 'certemyToDoWorkflowPrimaryVerification',
    url: 'management/certemy_tasks/primary_verification',
    action: 'access',
  },
  certemyToDoWorkflowSecondaryVerification: {
    name: 'certemyToDoWorkflowSecondaryVerification',
    url: 'management/certemy_tasks/secondary_verification',
    action: 'access',
  },
};

@Injectable()
export class WorkflowCertemyTasksToDoListPolicyGuardService  {
  constructor(private router: Router, private policyPermissionsService: PolicyPermissionsService) {}

  canActivate() {
    return this.policyPermissionsService
      .hasMultiplePermissions(Object.values(CERTEMY_TASKS_VERIFICATION_LIST_POLICIES))
      .pipe(
        take(1),
        map((permissions: IPermissions<CERTEMY_TASKS_VERIFICATION_LIST_POLICIES_KEYS>) => {
          if (permissions.certemyToDoWorkflowVerificationList) {
            return true;
          }

          this.router.navigate(['/access-restricted']);
          return false;
        }),
      );
  }
}

export const workflowCertemyTasksToDoListPolicyGuard: CanActivateFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
    return inject(WorkflowCertemyTasksToDoListPolicyGuardService).canActivate();
};

@Injectable()
export class WorkflowCertemyTasksPrimaryVerificationListPolicyGuardService  {
  constructor(private router: Router, private policyPermissionsService: PolicyPermissionsService) {}

  canActivate() {
    return this.policyPermissionsService
      .hasMultiplePermissions(Object.values(CERTEMY_TASKS_VERIFICATION_LIST_POLICIES))
      .pipe(
        take(1),
        map((permissions: IPermissions<CERTEMY_TASKS_VERIFICATION_LIST_POLICIES_KEYS>) => {
          if (permissions.certemyToDoWorkflowPrimaryVerification) {
            return true;
          }

          this.router.navigate(['/access-restricted']);
          return false;
        }),
      );
  }
}

export const workflowCertemyTasksPrimaryVerificationListPolicyGuard: CanActivateFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
    return inject(WorkflowCertemyTasksPrimaryVerificationListPolicyGuardService).canActivate();
};

@Injectable()
export class WorkflowCertemyTasksSecondaryVerificationListPolicyGuardService  {
  constructor(private router: Router, private policyPermissionsService: PolicyPermissionsService) {}

  canActivate() {
    return this.policyPermissionsService
      .hasMultiplePermissions(Object.values(CERTEMY_TASKS_VERIFICATION_LIST_POLICIES))
      .pipe(
        take(1),
        map((permissions: IPermissions<CERTEMY_TASKS_VERIFICATION_LIST_POLICIES_KEYS>) => {
          if (permissions.certemyToDoWorkflowSecondaryVerification) {
            return true;
          }

          this.router.navigate(['/access-restricted']);
          return false;
        }),
      );
  }
}

export const workflowCertemyTasksSecondaryVerificationListPolicyGuard: CanActivateFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
    return inject(WorkflowCertemyTasksSecondaryVerificationListPolicyGuardService).canActivate();
};
